import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import logger from '../lib/logger';
import {
  Link2, Copy, Check, Trash2, Eye, Clock, Shield, Loader2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPIRY_OPTIONS = [
  { value: '24', label: '24 hours' },
  { value: '168', label: '7 days' },
  { value: '720', label: '30 days' },
  { value: '0', label: 'Never expires' },
];

const VISIBILITY_OPTIONS = [
  { value: 'full', label: 'Full Details', desc: 'All components, modifiers, and role breakdowns' },
  { value: 'summary', label: 'Summary Only', desc: 'Total cost, effort, and key metrics' },
  { value: 'custom', label: 'Custom', desc: 'Choose exactly what to include' },
];

export default function ShareLinkDialog({ open, onOpenChange, estimateId, estimateName }) {
  const [tab, setTab] = useState('create');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  const [expiry, setExpiry] = useState('168');
  const [visibility, setVisibility] = useState('full');
  const [includeComponents, setIncludeComponents] = useState(true);
  const [includeModifiers, setIncludeModifiers] = useState(true);
  const [includeRoleBreakdown, setIncludeRoleBreakdown] = useState(true);
  const [includeCostDetails, setIncludeCostDetails] = useState(true);
  const [note, setNote] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');

  useEffect(() => {
    if (open && estimateId) {
      fetchLinks();
      setGeneratedUrl('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, estimateId]);

  const fetchLinks = async () => {
    setLoadingLinks(true);
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/shares`, { withCredentials: true });
      setLinks(res.data.links || []);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoadingLinks(false);
    }
  };

  const createLink = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/estimates/${estimateId}/share`, {
        expiry_hours: parseInt(expiry),
        visibility,
        include_components: includeComponents,
        include_modifiers: includeModifiers,
        include_role_breakdown: includeRoleBreakdown,
        include_cost_details: includeCostDetails,
        note: note || null,
      }, { withCredentials: true });
      const fullUrl = `${window.location.origin}/shared/${res.data.token}`;
      setGeneratedUrl(fullUrl);
      toast.success('Share link created');
      fetchLinks();
    } catch (err) {
      toast.error('Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const revokeLink = async (token) => {
    try {
      await axios.delete(`${API_URL}/api/estimates/${estimateId}/share/${token}`, { withCredentials: true });
      toast.success('Link revoked');
      fetchLinks();
    } catch (err) {
      toast.error('Failed to revoke link');
    }
  };

  const copyToClipboard = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return 'Never';
    const exp = new Date(expiresAt);
    const now = new Date();
    const diff = exp - now;
    if (diff <= 0) return 'Expired';
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
            <Link2 className="w-5 h-5 text-[#4ABFCF]" />
            Share Estimate Link
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">{estimateName}</p>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 -mx-6 px-6" data-testid="share-tabs">
          <button
            onClick={() => setTab('create')}
            className={`pb-2 px-3 text-sm font-medium border-b-2 transition-colors ${tab === 'create' ? 'border-[#4ABFCF] text-[#0F2040]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            data-testid="share-tab-create"
          >
            Create Link
          </button>
          <button
            onClick={() => setTab('manage')}
            className={`pb-2 px-3 text-sm font-medium border-b-2 transition-colors ${tab === 'manage' ? 'border-[#4ABFCF] text-[#0F2040]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
            data-testid="share-tab-manage"
          >
            Active Links ({links.filter(l => !l.is_expired).length})
          </button>
        </div>

        {tab === 'create' && (
          <div className="space-y-4 pt-2">
            {/* Expiry */}
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Link Expiry</Label>
              <Select value={expiry} onValueChange={setExpiry}>
                <SelectTrigger className="h-9" data-testid="share-expiry-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRY_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Visibility */}
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">What to Share</Label>
              <div className="grid grid-cols-3 gap-2">
                {VISIBILITY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setVisibility(opt.value)}
                    className={`p-2.5 rounded-lg border text-left transition-all ${
                      visibility === opt.value
                        ? 'border-[#4ABFCF] bg-[#4ABFCF]/5 ring-1 ring-[#4ABFCF]/20'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`share-visibility-${opt.value}`}
                  >
                    <div className="text-xs font-medium text-[#0F2040]">{opt.label}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5 leading-tight">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom options */}
            {visibility === 'custom' && (
              <div className="space-y-2.5 p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid="share-custom-options">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-600">Component Breakdown</Label>
                  <Switch checked={includeComponents} onCheckedChange={setIncludeComponents} data-testid="share-toggle-components" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-600">Modifier Details</Label>
                  <Switch checked={includeModifiers} onCheckedChange={setIncludeModifiers} data-testid="share-toggle-modifiers" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-600">Role Allocation</Label>
                  <Switch checked={includeRoleBreakdown} onCheckedChange={setIncludeRoleBreakdown} data-testid="share-toggle-roles" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-600">Cost Details by Group/Role</Label>
                  <Switch checked={includeCostDetails} onCheckedChange={setIncludeCostDetails} data-testid="share-toggle-costs" />
                </div>
              </div>
            )}

            {/* Note */}
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Note (optional)</Label>
              <Input
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="e.g. For executive review"
                className="h-9 text-sm"
                data-testid="share-note-input"
              />
            </div>

            {/* Generate */}
            <Button
              onClick={createLink}
              disabled={creating}
              className="w-full bg-[#4ABFCF] hover:bg-[#3AAAB9] h-9"
              data-testid="share-generate-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Generate Share Link
            </Button>

            {/* Generated URL */}
            {generatedUrl && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200" data-testid="share-generated-url">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-xs font-medium text-green-700">Link created!</span>
                </div>
                <div className="flex gap-2">
                  <Input value={generatedUrl} readOnly className="text-xs h-8 bg-white font-mono" data-testid="share-url-input" />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(generatedUrl, 'new')}
                    className="shrink-0 h-8"
                    data-testid="share-copy-btn"
                  >
                    {copied === 'new' ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(generatedUrl, '_blank')}
                    className="shrink-0 h-8"
                    data-testid="share-open-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'manage' && (
          <div className="space-y-3 pt-2 max-h-80 overflow-y-auto">
            {loadingLinks ? (
              <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
            ) : links.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No active share links</p>
            ) : (
              links.map((link) => (
                <div
                  key={link.token}
                  className={`p-3 rounded-lg border ${link.is_expired ? 'bg-red-50/50 border-red-100' : 'bg-white border-slate-100'}`}
                  data-testid={`share-link-${link.token.slice(0, 8)}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">
                        <Shield className="w-3 h-3 mr-1" />
                        {link.visibility?.level || 'full'}
                      </Badge>
                      {link.is_expired ? (
                        <Badge className="bg-red-100 text-red-600 text-[10px]">Expired</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-600 text-[10px]">Active</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                      <Eye className="w-3 h-3" /> {link.view_count} views
                    </div>
                  </div>

                  <div className="flex items-center gap-1 text-[10px] text-slate-400 mb-2">
                    <Clock className="w-3 h-3" />
                    {formatExpiry(link.expires_at)}
                    {link.note && <span className="ml-2 text-slate-500">"{link.note}"</span>}
                  </div>

                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-1"
                      onClick={() => copyToClipboard(`${window.location.origin}/shared/${link.token}`, link.token)}
                      disabled={link.is_expired}
                      data-testid={`share-copy-${link.token.slice(0, 8)}`}
                    >
                      {copied === link.token ? <Check className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />}
                      Copy
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs text-red-500 hover:text-red-600 hover:border-red-200"
                      onClick={() => revokeLink(link.token)}
                      data-testid={`share-revoke-${link.token.slice(0, 8)}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Revoke
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
